import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AutoCompleteResult, ButtonBaseType, ID, Maybe, Result } from 'src/models';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { P1 } from 'src/components/Typography';
import { ifTrue } from 'src/tools/logic.tools';
import { t } from 'src/tools/text.tools';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';
import { useDebouncedFn } from 'src/hooks';
import { requestLineItemForTransferAutoCompete } from 'src/store/lineItemsTransfers/lineItemsTransfers.actions';
import { TransferData } from 'src/contexts/lineItemsTransfers.context';

import classes from './LineItemTransferAutoComplete.module.scss';

const TXT = t(['lineItemTransferAutoComplete']);

interface Props {
  transferData: TransferData;
  lineItemsIds?: ID[];
}

export function LineItemTransferAutoComplete({ transferData, lineItemsIds = [] }: Props) {
  const {
    onChange,
    classNames,
    onSubmit = mockFn,
    onDiscard = mockFn,
    isDisabled,
    isAutoFocus,
    onClick,
    data: label,
    customization = {},
    actions = {},
    placeholder,
  } = useContext(InputContext);

  const { renderDropdownLabel, isInline, isXSmall, isSmallMenu, dropdownMinWidth = 'container-width' } = customization;

  const { onAddLineItem, onChangeInput } = actions;

  const [inputValue, setInputValue] = useState<Maybe<string>>();

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);

  const [isRequested, setIsRequested] = useState(false);

  const [results, setResults] = useState<AutoCompleteResult[]>([]);

  const skipResponse = useRef(false);

  const isLoading = useSelector(getCustomIsLoading)('lineItemsTransfersAutocomplete');

  useEffect(() => {
    if (label) setInputValue(label);
  }, [label]);

  const handleResults = useCallback(
    (response: Result) => {
      if (skipResponse.current || !response.isOK) return;

      const filteredResults = (response.payload as AutoCompleteResult[]).filter(i => !lineItemsIds.includes(i.id));

      setIsRequested(true);
      setResults(filteredResults);
    },
    [lineItemsIds],
  );

  const sendRequest = useCallback(
    (query: string) => {
      if (!skipResponse.current) {
        dispatch(
          requestLineItemForTransferAutoCompete(
            {
              ...transferData,
              query,
            },
            { callback: handleResults },
          ),
        );
      }
    },
    [dispatch, handleResults, transferData],
  );

  const request = useDebouncedFn(sendRequest, 600);

  const onChangeHandler = useCallback(
    (value: string, minLength = 2) => {
      if (value.length >= minLength) {
        request(value);
        skipResponse.current = false;
      } else if (!value) {
        setIsRequested(false);
        setResults([]);
        skipResponse.current = true;
      }
    },
    [request],
  );

  const allResults = useMemo(() => {
    if (onAddLineItem && isRequested) return [...results, { id: 'add', label: TXT('addNewLineItem') }];

    return results;
  }, [onAddLineItem, results, isRequested]);

  useEffect(() => {
    if (isDisabled) setInputValue(undefined);
  }, [isDisabled]);

  function handleOnAddLineItem(id: ID, label: string) {
    if (onChangeInput) onChangeInput(label);

    setInputValue(label);

    onChange({ id, label }, true);
  }

  function handleOnItemClick(id: ID) {
    setIsOpen(false);

    if (id === 'add' && onAddLineItem) onAddLineItem(inputValue, handleOnAddLineItem);
    else {
      const item = results.find(i => i.id === id);

      if (onChangeInput) onChangeInput(item?.label);

      setInputValue(item?.label);

      onChange(item, true);
    }
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnChange(userInput: string) {
    setIsOpen(true);
    onChange(undefined);
    setInputValue(userInput);
    onChangeHandler(userInput);

    if (onChangeInput) onChangeInput(userInput);
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnDiscard() {
    setIsOpen(false);

    onDiscard();
  }

  function handleOnSubmit() {
    setIsOpen(false);

    onSubmit();
  }

  function renderCustomLabel({ id, label }: ButtonBaseType) {
    if (id === 'add') return <P1 className={classes['add-new']}>{label}</P1>;

    return label;
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const iconId = baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        className={classNames?.input}
        onChange={handleOnChange}
        onDiscard={handleOnDiscard}
        onSubmit={handleOnSubmit}
        onClick={getHandleOnClick(baseComponentProps.onClick)}
        isLoading={isLoading && isOpen}
        value={inputValue}
        onFocus={baseComponentProps.onFocus}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={ifTrue(results.length, iconId)}
        isXSmall={!!isXSmall}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={allResults}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      isOpen={isOpen && !isLoading}
      onRequestOpenClose={handleOnRequestOpenClose}
      renderItemLabel={renderDropdownLabel ?? renderCustomLabel}
      isSmall={!!isSmallMenu}
      isInline={!!isInline}
      dropdownMinWidth={dropdownMinWidth}
    />
  );
}
